import { NavigateFunction } from 'react-router-dom';
import API_CONSTANT from '../constants/APIConstant';
import { get, ApiResult } from './apiClient';

export interface Product {
    priority: number;
    name: string;
    img: string;
    deeplink: string;
    lucideIcon?: string;
    // Plan feature this product belongs to; null/absent = core, always available.
    featureCode?: string | null;
    // Society admin only: the plan doesn't include featureCode, so the tile opens the subscription page.
    locked?: boolean;
}

export interface ProductsMap {
    [key: string]: Product[];
}

export interface ApiResponse {
    productsMap: ProductsMap;
}

const HOMEPAGE_CACHE_KEY = 'homepage_products_cache_v3';
const ALLMENU_CACHE_KEY  = 'allmenu_products_cache_v3';
const BOTTOM_NAV_CACHE_KEY = 'bottom_nav_products_cache_v2';
const BOTTOM_NAV_TTL_MS = 30 * 60 * 1000;

export interface CachedHomepageProducts {
    productsMap: ProductsMap;
    cachedAt: number; // epoch ms
}

const readCache = (key: string): CachedHomepageProducts | null => {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as CachedHomepageProducts;
        return parsed?.productsMap ? parsed : null;
    } catch {
        return null;
    }
};

const writeCache = (key: string, productsMap: ProductsMap) => {
    try {
        localStorage.setItem(key, JSON.stringify({ productsMap, cachedAt: Date.now() }));
    } catch { }
};

export const getCachedAllMenuProducts = (): CachedHomepageProducts | null =>
    readCache(ALLMENU_CACHE_KEY);

export const getCachedHomepageProducts = (): CachedHomepageProducts | null =>
    readCache(HOMEPAGE_CACHE_KEY);

export const getProductAPI = async (
    navigate: NavigateFunction,
    scope: string,
    allVisible: boolean,
    ttlMs?: number
) => {
    const qp        = !allVisible ? 'homepage' : scope || '';
    const cacheKey  = allVisible ? ALLMENU_CACHE_KEY : HOMEPAGE_CACHE_KEY;

    // Return cached data if still within TTL
    if (ttlMs && ttlMs > 0) {
        const cached = readCache(cacheKey);
        if (cached && (Date.now() - cached.cachedAt) < ttlMs) {
            return { productsMap: cached.productsMap } as ApiResponse;
        }
    }

    const path = `${API_CONSTANT.PATH.PRODUCT.GET}?scope=${encodeURIComponent(qp)}`;
    const res: ApiResult<ApiResponse> = await get<ApiResponse>(navigate, path);
    if (res.success) {
        if (res.data?.productsMap) writeCache(cacheKey, res.data.productsMap);
        return res.data as any;
    }
    return null;
}

/**
 * The bottom nav's tab list, product_type='BOTTOM_NAV' — cached 30 min in localStorage
 * (nav composition rarely changes) so the persistent Footer can render instantly from
 * cache on every mount instead of re-fetching. Logout (localStorage.clear()) and society
 * switch (SocietySwitcherSheet's blanket non-token wipe) already clear this along with
 * every other cached key, since a different account/society can have a different nav.
 */
export const getCachedBottomNav = (): Product[] | null => {
    const cached = readCache(BOTTOM_NAV_CACHE_KEY);
    return cached?.productsMap['BOTTOM_NAV'] ?? null;
};

export const getBottomNavAPI = async (navigate: NavigateFunction): Promise<Product[]> => {
    const cached = readCache(BOTTOM_NAV_CACHE_KEY);
    if (cached && (Date.now() - cached.cachedAt) < BOTTOM_NAV_TTL_MS) {
        return cached.productsMap['BOTTOM_NAV'] ?? [];
    }

    const path = `${API_CONSTANT.PATH.PRODUCT.GET}?scope=${encodeURIComponent('BOTTOM_NAV')}`;
    const res: ApiResult<ApiResponse> = await get<ApiResponse>(navigate, path);
    if (res.success && res.data?.productsMap) {
        writeCache(BOTTOM_NAV_CACHE_KEY, res.data.productsMap);
        return res.data.productsMap['BOTTOM_NAV'] ?? [];
    }
    // Fetch failed — fall back to a stale cache rather than an empty nav, if one exists.
    return cached?.productsMap['BOTTOM_NAV'] ?? [];
}